import { z } from 'zod';

import { PlaceIdSchema, GeoLocationSchema } from '@brightlocal/shared';

// Request schema for Google Places autocomplete
export const AutocompleteRequestSchema = z.object({
  input: z.string().min(1),
  sessionToken: z.string().optional(),
  includePureServiceAreaBusinesses: z.boolean().optional(),
  includedPrimaryTypes: z.array(z.string()).optional(),
  includedRegionCodes: z.array(z.string()).optional(),
  languageCode: z.string().optional()
});

// Response schema for Google Places autocomplete
const TextMatchSchema = z.object({
  startOffset: z.number().optional(),
  endOffset: z.number()
});

// Schema for the formatted text of a place prediction
const FormattedTextSchema = z.object({
  text: z.string(),
  matches: z.array(TextMatchSchema).optional()
});

// Schema for a single place prediction
export const PlacePredictionSchema = z.object({
  place: z.string(),
  placeId: PlaceIdSchema,
  text: FormattedTextSchema,
  // Schema for the structured format of a place prediction
  structuredFormat: z.object({
    mainText: FormattedTextSchema,
    secondaryText: FormattedTextSchema.optional()
  }),
  types: z.array(z.string())
});

// Schema of the response of a Google Places autocomplete request
export const AutocompleteResponseSchema = z
  .object({
    suggestions: z
      .array(
        z.object({
          placePrediction: PlacePredictionSchema
        })
      )
      .optional()
  })
  .transform((data) => ({
    suggestions: data.suggestions ?? []
  }));

// Define allowed by Google Place Details API fields
export const PLACE_DETAILS_FIELDS = [
  'id',
  'name',
  'displayName',
  'formattedAddress',
  'location',
  'pureServiceAreaBusiness',
  'addressComponents',
  'adrFormatAddress',
  'businessStatus',
  'types',
  'googleMapsUri',
  'websiteUri',
  'phoneNumber',
  'internationalPhoneNumber',
  'rating',
  'userRatingCount',
  'reviews',
  'photos',
  'currentOpeningHours',
  'regularOpeningHours',
  'utcOffsetMinutes',
  'priceLevel',
  'viewport'
] as const;

// Default fields for Place Details to be requested by default
export const DEFAULT_PLACE_DETAILS_FIELDS: readonly PlaceDetailsField[] = [
  'id',
  'displayName',
  'pureServiceAreaBusiness',
  'location',
  'addressComponents'
] as const;

export type PlaceDetailsField = (typeof PLACE_DETAILS_FIELDS)[number];

// Request schema for Google Places Place Details
export const PlaceDetailsRequestSchema = z.object({
  placeId: PlaceIdSchema,
  fields: z.array(z.enum(PLACE_DETAILS_FIELDS)).optional(),
  languageCode: z.string().optional(),
  sessionToken: z.string().optional()
});

// Response schema for Google Places Place Details
const DisplayNameSchema = z.object({
  text: z.string(),
  languageCode: z.string().optional()
});

// Schema for an address components record returned by Google Place Details API
const AddressComponentSchema = z.object({
  longText: z.string().optional(),
  shortText: z.string().optional(),
  types: z.array(z.string()).optional(),
  languageCode: z.string().optional()
});

// Schema of the response of a Google Places Place Details request
export const PlaceDetailsResponseSchema = z.object({
  name: z.string().optional(),
  id: PlaceIdSchema,
  displayName: DisplayNameSchema.optional(),
  formattedAddress: z.string().optional(),
  location: GeoLocationSchema,
  pureServiceAreaBusiness: z.boolean().optional(),
  addressComponents: z.array(AddressComponentSchema).optional()
});

export type AutocompleteRequest = z.infer<typeof AutocompleteRequestSchema>;
export type PlacePrediction = z.infer<typeof PlacePredictionSchema>;
export type AutocompleteResponse = z.infer<typeof AutocompleteResponseSchema>;
export type PlaceDetailsRequest = z.infer<typeof PlaceDetailsRequestSchema>;
export type PlaceDetailsResponse = z.infer<typeof PlaceDetailsResponseSchema>;
